import React, { useState, useEffect } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import useConfirmDialog from "services/hooks/useConfirmDialog";
import {
  Add as AddIcon,
  AccessTime,
  Phone,
  Email,
  Event,
  Description,
  Notifications,
  TrendingUp,
  Person,
  Edit,
  Delete,
  PhoneMissed,
  ExpandMore,
} from "@mui/icons-material";
import { format } from "date-fns";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector, useDispatch } from "react-redux";
import { setCallCompleteContactId } from "services/Redux/userToken";
import axios from "axios";

const activityIcons = {
  Call: <Phone />,
  Email: <Email />,
  Meeting: <Event />,
  Note: <Description />,
  Reminder: <Notifications />,
  Status_change: <TrendingUp />,
  Created: <Person />,
  Updated: <Edit />,
  Missed_call: <PhoneMissed />,
};

// const activityColors = {
//   Call: "success",
//   Email: "primary",
//   Meeting: "secondary",
//   Note: "info",
//   Reminder: "warning",
//   Status_change: "info",
//   Created: "success",
//   Updated: "warning",
// };

const formatCallDuration = (value) => {
  if (!value && value !== 0) return "—";
  if (typeof value === "string" && value.includes(":")) {
    const parts = value.split(":").map(Number);
    if (parts.length === 3) return `${parts[0] * 60 + parts[1]}m ${parts[2]}s`;
    if (parts.length === 2) return `${parts[0]}m ${parts[1]}s`;
  }
  const s = Math.max(0, Math.floor(Number(value)));
  if (isNaN(s)) return "—";
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// const activityTypes = [
//   { value: "call", label: "Phone Call" },
//   { value: "email", label: "Email" },
//   { value: "meeting", label: "Meeting" },
//   { value: "note", label: "Note" },
//   { value: "reminder", label: "Reminder" },
// ];

export default function ContactTimeline({
  contactId,
  triggerAddActivity,
  onAddActivityOpened,
  refreshKey,
}) {
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingActivity, setEditingActivity] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();
  const [activityTypes, setActivityTypes] = useState([]);
  const [reminderActivityTypeId, setReminderActivityTypeId] = useState(null);
  const [reminderTypes, setReminderTypes] = useState([]);
  const [defaultActivityTypeId, setDefaultActivityTypeId] = useState("");
  const getLocalDateTimeString = (date = new Date()) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offsetMs);
    return localDate.toISOString().slice(0, 16);
  };
  const initialActivity = {
    type: defaultActivityTypeId,
    title: "",
    description: "",
    date: getLocalDateTimeString(),
  };
  const [newActivity, setNewActivity] = useState(initialActivity);
  const dispatch = useDispatch();
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer
  );
  const callCompleteContactId = useSelector(
    (state) => state.tokenReducer.callCompleteContactId
  );

  const loadActivities = React.useCallback(
    async (pageNumber = 1) => {
      setLoading(true);
      try {
        const res = await customGetAPI(
          `${domain}crm/activities/?contact_id=${contactId}&page=${pageNumber}`
        );
        setActivities(res.responseDetails.results);
        setTotalPages(Math.ceil(res.responseDetails.count / 10));
      } catch (err) {
        console.error("Failed to load activities", err);
      } finally {
        setLoading(false);
      }
    },
    [domain, contactId]
  );

  useEffect(() => {
    if (contactId) loadActivities();
  }, [contactId, loadActivities, refreshKey]);

  useEffect(() => {
    if (
      callCompleteContactId != null &&
      Number(callCompleteContactId) === Number(contactId)
    ) {
      loadActivities(1);
      setPage(1);
      dispatch(setCallCompleteContactId(null));
    }
  }, [callCompleteContactId, contactId, loadActivities, dispatch]);

  useEffect(() => {
    const localDatetime = getLocalDateTimeString(); // ISO string without timezone
    setNewActivity((prev) => ({ ...prev, date: localDatetime }));
  }, []);

  useEffect(() => {
    if (triggerAddActivity) {
      setShowAddForm(true);
      if (onAddActivityOpened) onAddActivityOpened();
    }
  }, [onAddActivityOpened, triggerAddActivity]);

  useEffect(() => {
    const fetchActivityTypeOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/activity-type/`);
        const types = res.responseDetails?.results || [];
        setActivityTypes(types);
        if (types.length > 0) {
          const noteType = types.find((t) => t.label?.toLowerCase() === "note");
          const defaultTypeId = noteType ? noteType.id : types[0].id;
          setDefaultActivityTypeId(defaultTypeId);
          setNewActivity((prev) => ({
            ...prev,
            type: defaultTypeId,
          }));
          const reminderType = types.find(
            (t) => t.label?.toLowerCase() === "reminder"
          );
          setReminderActivityTypeId(reminderType?.id || null);
        }
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    fetchActivityTypeOptions();
  }, [domain]);

  useEffect(() => {
    loadActivities(page);
  }, [page, loadActivities]);

  useEffect(() => {
    const fetchReminderTypes = async () => {
      const res = await customGetAPI(`${domain}crm/reminder-type/`);
      setReminderTypes(res.responseDetails?.results || []);
    };
    fetchReminderTypes();
  }, [domain]);

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      const now = getLocalDateTimeString();
      if (editingActivity) {
        // PATCH
        await axios.patch(
          `${domain}crm/activities/${editingActivity.id}/`,
          {
            ...newActivity,
          },
          //    {
          //   ...newActivity,
          //   date:
          //     newActivity.type === reminderActivityTypeId
          //       ? now // activities always store now
          //       : now,
          // },
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );
      } else {
        // POST
        await axios.post(
          `${domain}crm/activities/`,
          {
            ...newActivity,
            date: newActivity.type === reminderActivityTypeId ? now : now,
            contact: contactId,
          },
          {
            headers: {
              Authorization: `token ${token}`,
            },
          }
        );

        if (newActivity.type === reminderActivityTypeId) {
          const followUpType = reminderTypes.find(
            (t) => t.label?.toLowerCase() === "follow up"
          );
          const reminderTypeId = followUpType
            ? followUpType.id
            : reminderTypes[0]?.id || "";
          const reminderPayload = {
            title: newActivity.title,
            notes: newActivity.description || "",
            date: newActivity.date,
            type: reminderTypeId,
            priority: "medium",
            contact: contactId,
            assigned_to: userDetails?.id,
          };

          await axios.post(`${domain}crm/reminders/`, reminderPayload, {
            headers: {
              Authorization: `token ${token}`,
            },
          });
        }
      }

      setShowAddForm(false);
      setEditingActivity(null);
      setNewActivity(initialActivity);
      loadActivities();
    } catch (err) {
      console.error("Failed to save activity", err);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Timeline</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(!showAddForm)}
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
        >
          Add Activity
        </Button>
      </Box>

      {showAddForm && (
        <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
          <form onSubmit={handleAddActivity}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={newActivity.type}
                  label="Activity Type"
                  onChange={(e) =>
                    setNewActivity({ ...newActivity, type: e.target.value })
                  }
                >
                  {activityTypes.map((type) => (
                    <MenuItem key={type.value} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="datetime-local"
                label="Date"
                fullWidth
                value={newActivity.date}
                onChange={(e) =>
                  setNewActivity({ ...newActivity, date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                disabled={
                  editingActivity !== null ||
                  newActivity.type !== reminderActivityTypeId
                }
              />
            </Box>
            <TextField
              fullWidth
              label="Title"
              value={newActivity.title}
              onChange={(e) =>
                setNewActivity({ ...newActivity, title: e.target.value })
              }
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newActivity.description}
              onChange={(e) =>
                setNewActivity({ ...newActivity, description: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={2}>
              <Button variant="contained" type="submit">
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAddForm(false);
                  setNewActivity(initialActivity);
                  setEditingActivity(null);
                }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      )}
      {loading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
          <Typography mt={2}>Loading timeline...</Typography>
        </Box>
      ) : activities?.length > 0 ? (
        <Timeline position="right">
          {activities.map((activity, index) => {
            const dateKey = format(new Date(activity.date), "d MMM yyyy");
            const prevDateKey =
              index > 0
                ? format(new Date(activities[index - 1].date), "d MMM yyyy")
                : null;
            const showDateSeparator = index === 0 || dateKey !== prevDateKey;
            return (
              <React.Fragment key={activity.id}>
                {showDateSeparator && (
                  <Box display="flex" justifyContent="center" my={1}>
                    <Chip label={dateKey} size="small" variant="outlined" />
                  </Box>
                )}
                <TimelineItem>
              <TimelineOppositeContent sx={{ flex: 0.25, pt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                  {format(new Date(activity.date), "MMM d, yyyy h:mm a")}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    backgroundColor:
                      activity.activity_name === "Call" && !activity.completed
                        ? "#f44336"
                        : activity.status_color,
                    color: "#fff",
                  }}
                >
                  {activity.activity_name === "Call" && !activity.completed
                    ? activityIcons.Missed_call
                    : activityIcons[activity.activity_name] || <Description />}
                </TimelineDot>
                {index < activities.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Box
                  position="relative"
                  onMouseEnter={() => setHoveredId(activity.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      transition: "0.2s",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    {/* Title */}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {activity.title}
                    </Typography>

                    {/* Icons shown only on hover */}
                    {hoveredId === activity.id && (
                      <Box
                        position="absolute"
                        top={8}
                        right={8}
                        display="flex"
                        gap={1}
                        zIndex={1}
                      >
                        <IconButton
                          size="small"
                          title="Edit Activity"
                          onClick={() => {
                            const dateTimeString = `${
                              activity.formatted_date
                            }T${activity.formatted_time.slice(0, 5)}`;
                            setNewActivity({
                              type: activity.type,
                              title: activity.title,
                              description: activity.description || "",
                              // date: new Date(activity.date)
                              //   .toISOString()
                              //   .slice(0, 16),
                              date: dateTimeString,
                            });
                            setEditingActivity(activity);
                            setShowAddForm(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          title="Delete Activity"
                          onClick={() => {
                            setShowAddForm(false);
                            setEditingActivity(null);
                            showConfirmDialog({
                              title: "Delete Activity",
                              description: `Are you sure you want to delete "${activity.title}"?`,
                              onConfirm: async () => {
                                try {
                                  await axios.delete(
                                    `${domain}crm/activities/${activity.id}/`,
                                    {
                                      headers: {
                                        Authorization: `token ${token}`,
                                      },
                                    }
                                  );
                                  loadActivities();
                                } catch (err) {
                                  console.error(
                                    "Failed to delete activity:",
                                    err?.response?.data || err.message
                                  );
                                }
                              },
                            });
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}

                    {activity.call_detail ? (
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                          mt: 1,
                          bgcolor: "transparent",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            {activity.call_outcome && (
                              <Chip
                                label={activity.call_outcome}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {activity.completed ? (
                              <Chip label="Answered" size="small" color="success" />
                            ) : (
                              <Chip label="Missed" size="small" color="error" />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {activity.call_detail.called_by || "Unknown caller"}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            Outcome
                          </Typography>
                          <Grid container spacing={1} mt={0.25} mb={1.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Called by
                              </Typography>
                              <Typography variant="body2">
                                {activity.call_detail.called_by || "—"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Assigned to
                              </Typography>
                              <Typography variant="body2">
                                {activity.call_detail.assigned_to || "—"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Phone
                              </Typography>
                              <Typography variant="body2">
                                {activity.call_detail.phone || "—"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Duration
                              </Typography>
                              <Typography variant="body2">
                                {formatCallDuration(activity.call_detail.duration)}
                              </Typography>
                            </Grid>
                          </Grid>
                          {activity.call_detail.notes && (
                            <Box sx={{ bgcolor: "#f9f9f9", p: 1, borderRadius: 1, mb: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Notes
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {activity.call_detail.notes}
                              </Typography>
                            </Box>
                          )}
                          {activity.call_detail.recording_url && (
                            <audio
                              controls
                              style={{ width: "100%" }}
                              src={activity.call_detail.recording_url}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <>
                        {activity.description && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {activity.description}
                          </Typography>
                        )}
                        {(activity.call_outcome || activity.call_notes) && (
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              Outcome
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                              {activity.call_outcome && (
                                <Chip label={activity.call_outcome} size="small" variant="outlined" />
                              )}
                            </Box>
                            {activity.call_notes && (
                              <Typography variant="body2" color="text.secondary" mt={0.5}>
                                {activity.call_notes}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </>
                    )}

                    {/* Chips */}
                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={activity.activity_name.replace(/_/g, " ")}
                        size="small"
                      />
                      {activity.activity_name === "Call" &&
                      !activity.completed ? (
                        <Chip label="Missed" size="small" color="error" />
                      ) : activity.completed ? (
                        <Chip label="Completed" size="small" color="success" />
                      ) : null}
                    </Box>
                  </Paper>
                </Box>
              </TimelineContent>
                </TimelineItem>
              </React.Fragment>
            );
          })}
        </Timeline>
      ) : (
        <Box textAlign="center" mt={4}>
          <AccessTime fontSize="large" color="disabled" />
          <Typography color="text.secondary" mt={1}>
            No activities recorded
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setShowAddForm(true)}
          >
            Add First Activity
          </Button>
        </Box>
      )}
      <ConfirmDialog />
      {activities.length > 0 && totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
          mt={2}
        >
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Showing page {page} of {totalPages}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
